/**
 * MessagingDrawer — Persistent slide-out messaging panel (Option B)
 *
 * Slides in from the right edge (desktop) or bottom (mobile).
 * Chat while the map is still visible. Channels listed in the drawer.
 *
 * Rendered at the Dashboard level, on top of the cosmic map.
 * Does NOT replace the full /messages page — that remains for deep browsing.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  MessageCircle, X, Hash, Lock, Users, Sparkles, Send,
  ArrowLeft, Reply, Loader2, Search
} from 'lucide-react';
import { cn } from '../utils/cn';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { useRealtime } from '../context/RealtimeContext';

// ============================================================================
// CONSTANTS & HELPERS
// ============================================================================

const EMOJI_QUICK = ['\u{1F44D}', '\u{2764}\u{FE0F}', '\u{1F602}', '\u{1F389}', '\u{1F525}', '\u{1F440}', '\u{1F4A1}', '\u{2705}'];

const CHANNEL_ICONS = {
  public: Hash,
  private: Lock,
  direct_message: MessageCircle,
  group_dm: Users,
  project: Sparkles,
  study_group: Users,
};

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const seconds = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (seconds < 60) return 'now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatMessageTime = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

// ============================================================================
// CHANNEL LIST ITEM
// ============================================================================

const ChannelListItem = ({ channel, isActive, onClick }) => {
  const Icon = CHANNEL_ICONS[channel.channelType || channel.channel_type] || Hash;
  const isDM = (channel.channelType || channel.channel_type) === 'direct_message';
  const isProject = (channel.channelType || channel.channel_type) === 'project';
  const displayName = isDM
    ? (channel.otherUser?.username || channel.display_name || channel.displayName || channel.name)
    : (channel.display_name || channel.displayName || channel.name);
  const unread = channel.unreadCount || channel.unread_count || 0;

  return (
    <button
      onClick={() => onClick(channel)}
      className={cn(
        'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left',
        'transition-all duration-100',
        isActive
          ? 'bg-purple-500/15 text-white'
          : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50'
      )}
    >
      {isDM ? (
        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center flex-shrink-0">
          <span className="text-[10px] font-bold text-white">
            {(displayName || '?').charAt(0).toUpperCase()}
          </span>
        </div>
      ) : (
        <Icon className={cn(
          'w-4 h-4 flex-shrink-0',
          isActive ? 'text-purple-400' : 'text-zinc-600',
          isProject && 'text-amber-500/70'
        )} />
      )}
      <span className={cn(
        'flex-1 truncate text-[13px]',
        unread > 0 && 'font-semibold text-white'
      )}>
        {displayName}
      </span>
      {unread > 0 && (
        <span className="min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px] font-bold text-white bg-purple-600 rounded-full">
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </button>
  );
};

// ============================================================================
// MESSAGE BUBBLE
// ============================================================================

const MessageBubble = ({ message, isOwn, onReact, onReply }) => {
  const [showActions, setShowActions] = useState(false);

  if (message.content_type === 'system' || message.contentType === 'system') {
    return (
      <div className="flex justify-center py-1.5">
        <span className="text-[11px] text-zinc-600 bg-zinc-800/30 px-3 py-1 rounded-full max-w-[85%] text-center">
          {message.content}
        </span>
      </div>
    );
  }

  const reactions = message.reactions || [];
  const username = message.username || message.author?.username || 'Unknown';

  return (
    <div
      className="group flex items-start gap-2.5 px-3 py-1 hover:bg-zinc-800/20 rounded-lg transition-colors"
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center flex-shrink-0 mt-0.5">
        <span className="text-[10px] font-bold text-white">
          {username.charAt(0).toUpperCase()}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className={cn(
            'text-[12px] font-semibold',
            isOwn ? 'text-purple-300' : 'text-zinc-300'
          )}>
            {username}
          </span>
          <span className="text-[10px] text-zinc-600">
            {formatMessageTime(message.created_at || message.createdAt)}
          </span>
        </div>
        <p className="text-[13px] text-zinc-300 leading-relaxed break-words whitespace-pre-wrap">
          {message.content}
        </p>
        {reactions.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {reactions.map((r, i) => (
              <button
                key={`${r.emoji}-${i}`}
                onClick={() => onReact?.(message.id, r.emoji)}
                className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-zinc-800/50 hover:bg-zinc-700/50 text-[11px] transition-colors"
              >
                <span>{r.emoji}</span>
                <span className="text-zinc-500">{r.count}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {showActions && (
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          {EMOJI_QUICK.slice(0, 3).map(emoji => (
            <button
              key={emoji}
              onClick={() => onReact?.(message.id, emoji)}
              className="p-1 rounded hover:bg-zinc-700/50 text-[12px]"
            >
              {emoji}
            </button>
          ))}
          <button
            onClick={() => onReply?.(message)}
            className="p-1 rounded hover:bg-zinc-700/50 text-zinc-500 hover:text-zinc-300"
          >
            <Reply className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MESSAGE COMPOSER
// ============================================================================

const MessageComposer = ({ onSend, channelName, replyTo, onCancelReply }) => {
  const [text, setText] = useState('');
  const inputRef = useRef(null);

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    onSend(trimmed, replyTo?.id || null);
    setText('');
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="border-t border-zinc-800/50 bg-zinc-900/80 px-3 py-2.5">
      {replyTo && (
        <div className="flex items-center gap-2 mb-2 px-2 py-1.5 bg-zinc-800/30 rounded-lg text-xs">
          <Reply className="w-3 h-3 text-purple-400" />
          <span className="text-zinc-500 truncate flex-1">
            Replying to <span className="text-zinc-300 font-medium">{replyTo.username || replyTo.author?.username}</span>
          </span>
          <button onClick={onCancelReply} className="text-zinc-600 hover:text-zinc-400">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <textarea
            ref={inputRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`Message ${channelName || ''}...`}
            rows={1}
            className={cn(
              'w-full bg-zinc-800/50 border border-zinc-700/50 rounded-xl',
              'px-3 py-2 text-[13px] text-zinc-200 placeholder:text-zinc-600',
              'focus:outline-none focus:ring-1 focus:ring-purple-500/50 focus:border-purple-500/30',
              'resize-none max-h-[100px]',
              'transition-all duration-150'
            )}
            style={{ minHeight: '36px' }}
            onInput={(e) => {
              e.target.style.height = '36px';
              e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
            }}
          />
        </div>
        <button
          onClick={handleSend}
          disabled={!text.trim()}
          className={cn(
            'p-2 rounded-xl transition-all duration-150',
            text.trim()
              ? 'bg-purple-600 text-white hover:bg-purple-500 shadow-sm shadow-purple-600/30'
              : 'bg-zinc-800 text-zinc-600 cursor-not-allowed'
          )}
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// MESSAGING DRAWER (main export)
// ============================================================================

const MessagingDrawer = ({
  isOpen,
  onClose,
  onNavigateToProject,
  initialChannelId = null,
}) => {
  const { user } = useAuth();
  const currentUserId = user?.id;
  const { subscribe, connectionStatus } = useRealtime();

  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [view, setView] = useState('channels');
  const [searchQuery, setSearchQuery] = useState('');

  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  // ---- Fetch channels ----
  const fetchChannels = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/messaging/channels');
      const data = res.data?.channels || res.data || [];
      setChannels(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to load channels:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  // ---- Fetch messages for channel ----
  const fetchMessages = useCallback(async (channelId) => {
    setLoadingMessages(true);
    try {
      const res = await api.get(`/messaging/channels/${channelId}/messages`, {
        params: { limit: 50 }
      });
      setMessages(res.data.messages || []);
    } catch (e) {
      console.error('Failed to load messages:', e);
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  // ---- Load channels on open ----
  useEffect(() => {
    if (isOpen) fetchChannels();
  }, [isOpen, fetchChannels]);

  // ---- Handle initial channel deep-link ----
  useEffect(() => {
    if (initialChannelId && channels.length > 0) {
      const ch = channels.find(c => c.id === parseInt(initialChannelId));
      if (ch) {
        setActiveChannel(ch);
        setView('chat');
      }
    }
  }, [initialChannelId, channels]);

  // ---- Load messages when channel changes ----
  useEffect(() => {
    if (activeChannel?.id) {
      fetchMessages(activeChannel.id);
    }
  }, [activeChannel?.id, fetchMessages]);

  // ---- Auto-scroll on new messages ----
  useEffect(() => {
    if (messagesEndRef.current && !loadingMessages) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, loadingMessages]);

  // ---- Live delivery via SSE ----------------------------------------
  // Replaces a 5-second poll. That poll cost four SQL round-trips plus a
  // last_read_at write and a commit EVERY five seconds per open drawer,
  // and almost always returned nothing. Thirty students in a classroom
  // was ~6 requests and ~30 queries per second against the database, all
  // to deliver nothing.
  //
  // It also had a subtle churn bug: `messages` was in its dependency
  // array, so every arriving message tore down and rebuilt the interval,
  // resetting the five-second clock. Under a fast conversation the poll
  // could be starved indefinitely.
  //
  // Events come from app/messaging_realtime_patch.py. The sender is
  // excluded server-side, since handleSendMessage already appends
  // optimistically — echoing would duplicate.
  const activeChannelIdRef = useRef(null);
  useEffect(() => { activeChannelIdRef.current = activeChannel?.id ?? null; }, [activeChannel?.id]);

  useEffect(() => {
    const unsubs = [];

    unsubs.push(subscribe('new_message', (data) => {
      // Only for the channel currently on screen. A message for another
      // channel belongs in that channel's unread count, not this list.
      const chId = data?.channel_id ?? data?.channelId;
      if (!chId || chId !== activeChannelIdRef.current) {
        fetchChannels();   // refresh unread badges
        return;
      }
      setMessages(prev =>
        // Guard against a double-append: an optimistic local copy plus
        // the broadcast, or two events for one message after a reconnect.
        prev.some(m => m.id === data.id) ? prev : [...prev, data]
      );
    }));

    unsubs.push(subscribe('message_edited', (data) => {
      const chId = data?.channel_id ?? data?.channelId;
      if (!chId || chId !== activeChannelIdRef.current) return;
      setMessages(prev => prev.map(m => (m.id === data.id ? { ...m, ...data } : m)));
    }));

    unsubs.push(subscribe('message_deleted', (data) => {
      const chId = data?.channel_id ?? data?.channelId;
      if (!chId || chId !== activeChannelIdRef.current) return;
      setMessages(prev => prev.filter(m => m.id !== data.id));
    }));

    return () => unsubs.forEach(u => u?.());
  }, [subscribe, fetchChannels]);

  // ---- Fallback poll, ONLY while the stream is down ------------------
  // SSE drops: laptop sleeps, wifi switches, a proxy times the connection
  // out. Without this the drawer would look alive and silently deliver
  // nothing. 15s rather than 5s because this is a safety net, not the
  // delivery mechanism.
  //
  // NOTE the dependency list: activeChannel.id and view only. `messages`
  // is deliberately absent — including it is what caused the churn bug
  // described above. The latest id is read from a ref instead.
  const messagesRef = useRef(messages);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  useEffect(() => {
    if (!activeChannel?.id || view !== 'chat') return;
    if (connectionStatus === 'connected') return;

    const poll = async () => {
      try {
        const current = messagesRef.current;
        const lastId = current.length > 0 ? current[current.length - 1].id : null;
        if (!lastId) return;
        const res = await api.get(`/messaging/channels/${activeChannel.id}/messages`, {
          params: { after: lastId, limit: 50 }
        });
        const newMsgs = res.data.messages || [];
        if (newMsgs.length > 0) {
          setMessages(prev => {
            const seen = new Set(prev.map(m => m.id));
            const fresh = newMsgs.filter(m => !seen.has(m.id));
            return fresh.length ? [...prev, ...fresh] : prev;
          });
        }
      } catch (e) { /* silent */ }
    };

    pollRef.current = setInterval(poll, 15000);
    return () => clearInterval(pollRef.current);
  }, [activeChannel?.id, view, connectionStatus]);

  // ---- Select channel ----
  const handleSelectChannel = (channel) => {
    setActiveChannel(channel);
    setView('chat');
    setReplyTo(null);
  };

  // ---- Send message ----
  const handleSendMessage = async (content, parentMessageId = null) => {
    if (!activeChannel?.id) return;

    // Parse @mentions from content
    const mentionRegex = /@(\w+)/g;
    const mentionedUsernames = [];
    let match;
    while ((match = mentionRegex.exec(content)) !== null) {
      mentionedUsernames.push(match[1]);
    }

    // Resolve usernames to IDs if we have channel member data
    const mentionedIds = [];
    if (mentionedUsernames.length > 0 && activeChannel.members) {
      for (const username of mentionedUsernames) {
        const member = activeChannel.members.find(
          m => m.username?.toLowerCase() === username.toLowerCase()
        );
        if (member) mentionedIds.push(member.user_id || member.userId);
      }
    }

    try {
      const res = await api.post(`/messaging/channels/${activeChannel.id}/messages`, {
        content,
        parent_message_id: parentMessageId,
        mentioned_users: mentionedIds,
      });
      const newMsg = res.data.data;
      if (newMsg) {
        setMessages(prev => [...prev, newMsg]);
      }
      setReplyTo(null);
    } catch (e) {
      console.error('Failed to send message:', e);
    }
  };

  // ---- React to message ----
  const handleReact = async (messageId, emoji) => {
    try {
      await api.post(`/messaging/messages/${messageId}/reactions`, { emoji });
      if (activeChannel?.id) fetchMessages(activeChannel.id);
    } catch (e) {
      console.error('Failed to react:', e);
    }
  };

  // ---- Back to channel list ----
  const handleBack = () => {
    setView('channels');
    setActiveChannel(null);
    setMessages([]);
    setReplyTo(null);
  };

  // ---- Navigate to project from project channel ----
  const handleProjectClick = () => {
    const pid = activeChannel?.project_id || activeChannel?.projectId;
    if (pid) onNavigateToProject?.(pid);
  };

  // ---- Filter channels by search ----
  const filteredChannels = useMemo(() => {
    if (!searchQuery.trim()) return channels;
    const q = searchQuery.toLowerCase();
    return channels.filter(ch => {
      const name = (ch.display_name || ch.displayName || ch.name || '').toLowerCase();
      return name.includes(q);
    });
  }, [channels, searchQuery]);

  // ---- Categorize channels ----
  const categorized = useMemo(() => {
    const dms = filteredChannels.filter(c =>
      (c.channelType || c.channel_type) === 'direct_message'
    );
    const project = filteredChannels.filter(c =>
      (c.channelType || c.channel_type) === 'project'
    );
    const pub = filteredChannels.filter(c =>
      !['direct_message', 'project'].includes(c.channelType || c.channel_type)
    );
    return { dms, project, pub };
  }, [filteredChannels]);

  const activeChannelName = activeChannel
    ? (activeChannel.display_name || activeChannel.displayName || activeChannel.name || '')
    : '';
  const isProjectChannel = activeChannel &&
    ((activeChannel.channelType || activeChannel.channel_type) === 'project');

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop — mobile only */}
      <div
        className="fixed inset-0 bg-black/30 z-40 md:hidden"
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={cn(
          'fixed z-50 bg-zinc-900 border-l border-zinc-800 shadow-2xl shadow-black/50',
          'flex flex-col',
          'transition-transform duration-300 ease-out',
          // Desktop: right panel
          'top-0 right-0 bottom-0 w-[380px]',
          // Mobile: bottom sheet override
          'max-md:top-auto max-md:left-0 max-md:right-0 max-md:bottom-0 max-md:w-full max-md:max-h-[80vh] max-md:rounded-t-2xl max-md:border-t max-md:border-l-0'
        )}
      >
        {/* Mobile drag handle */}
        <div className="md:hidden flex justify-center pt-2 pb-1">
          <div className="w-8 h-1 rounded-full bg-zinc-700" />
        </div>

        {/* ============ HEADER ============ */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800/50 flex-shrink-0">
          {view === 'chat' ? (
            <>
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <button
                  onClick={handleBack}
                  className="p-1 rounded-lg hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors flex-shrink-0"
                >
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-semibold text-white truncate">
                    {activeChannelName}
                  </h3>
                  {isProjectChannel && (
                    <button
                      onClick={handleProjectClick}
                      className="text-[10px] text-purple-400 hover:text-purple-300 transition-colors"
                    >
                      View on map &rarr;
                    </button>
                  )}
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-1 rounded-lg hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-purple-400" />
                <h3 className="text-sm font-semibold text-white">Messages</h3>
              </div>
              <button
                onClick={onClose}
                className="p-1 rounded-lg hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </>
          )}
        </div>

        {/* ============ BODY ============ */}
        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          {view === 'channels' ? (
            /* ---- CHANNEL LIST VIEW ---- */
            <div className="flex-1 overflow-y-auto">
              {/* Search */}
              <div className="px-3 py-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-600" />
                  <input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search channels..."
                    className="w-full bg-zinc-800/50 border border-zinc-700/40 rounded-lg pl-8 pr-3 py-1.5 text-[12px] text-zinc-300 placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-purple-500/40"
                  />
                </div>
              </div>

              {loading ? (
                <div className="flex justify-center py-10">
                  <Loader2 className="w-5 h-5 text-purple-500 animate-spin" />
                </div>
              ) : (
                <div className="px-2 pb-4 space-y-3">
                  {categorized.dms.length > 0 && (
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-600 px-2 mb-1">
                        Direct Messages
                      </p>
                      {categorized.dms.map(ch => (
                        <ChannelListItem
                          key={ch.id}
                          channel={ch}
                          isActive={activeChannel?.id === ch.id}
                          onClick={handleSelectChannel}
                        />
                      ))}
                    </div>
                  )}
                  {categorized.project.length > 0 && (
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-600 px-2 mb-1">
                        Project Channels
                      </p>
                      {categorized.project.map(ch => (
                        <ChannelListItem
                          key={ch.id}
                          channel={ch}
                          isActive={activeChannel?.id === ch.id}
                          onClick={handleSelectChannel}
                        />
                      ))}
                    </div>
                  )}
                  {categorized.pub.length > 0 && (
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-600 px-2 mb-1">
                        Channels
                      </p>
                      {categorized.pub.map(ch => (
                        <ChannelListItem
                          key={ch.id}
                          channel={ch}
                          isActive={activeChannel?.id === ch.id}
                          onClick={handleSelectChannel}
                        />
                      ))}
                    </div>
                  )}
                  {channels.length === 0 && !loading && (
                    <div className="flex flex-col items-center justify-center py-10 px-4">
                      <div className="w-12 h-12 rounded-2xl bg-zinc-800/30 flex items-center justify-center mb-3 ring-1 ring-zinc-800/50">
                        <MessageCircle className="w-5 h-5 text-zinc-600" />
                      </div>
                      <p className="text-sm text-zinc-400 text-center font-medium">No conversations yet</p>
                      <p className="text-xs text-zinc-600 text-center mt-1 max-w-[200px] leading-relaxed">
                        Fork a project or respond to a challenge to start collaborating.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            /* ---- CHAT VIEW ---- */
            <>
              <div className="flex-1 overflow-y-auto px-1 py-2 space-y-0.5">
                {loadingMessages ? (
                  <div className="flex justify-center py-10">
                    <Loader2 className="w-5 h-5 text-purple-500 animate-spin" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 px-4">
                    <p className="text-sm text-zinc-400 text-center font-medium">No messages yet</p>
                    <p className="text-xs text-zinc-600 text-center mt-1">Be the first to say something.</p>
                  </div>
                ) : (
                  <>
                    {messages.map(msg => (
                      <MessageBubble
                        key={msg.id}
                        message={msg}
                        isOwn={msg.user_id === currentUserId || msg.userId === currentUserId}
                        onReact={handleReact}
                        onReply={(m) => setReplyTo(m)}
                      />
                    ))}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>
              <MessageComposer
                onSend={handleSendMessage}
                channelName={activeChannelName}
                replyTo={replyTo}
                onCancelReply={() => setReplyTo(null)}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default MessagingDrawer;